<script setup lang="ts">
import { useHead } from '@vueuse/head'
import { computed, onMounted, ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { PrivacyShield } from '@toni/brand-ui'
import { useConfigStore } from '@toni/rendering/src/stores/config'
import { usePageStore } from '@toni/rendering/src/stores/page'
import { useSessionStore } from '@toni/rendering/src/stores/session'
import { useSourcepointStore } from '@toni/rendering/src/stores/sourcepoint'

import { queueAdEvent } from '../../../advertisement/utils/queue-ad-event'
import { useClient } from '../../../utils/client-only'
import { VideoContentBlock } from '../../../utils/content-block-schema'
import { createImgUrl } from '../../bauer-image/image-utils'
import ConsentWrapper from '../../consent/ConsentWrapper.vue'

declare global {
  interface Window {
    reloadVideoPlayer?: () => void
  }
}

const props = defineProps<{
  video: VideoContentBlock['video']
}>()

const intervalForFetchAndDispatchAdTargetingStringToPlayer: Ref<NodeJS.Timeout | null> = ref(null)
const sessionStore = useSessionStore()
const pageStore = usePageStore()
const configStore = useConfigStore()
const sourcepointStore = useSourcepointStore()
const { t } = useI18n()
const { isClient } = useClient()
const simulateContentPassOrContentPass = computed(() => sessionStore.simulateContentPassOrContentPass)
const headline = computed(() =>
  t(`privacyShield.video.headline.${simulateContentPassOrContentPass.value ? 'withPurSub' : 'withoutPurSub'}`)
)
const description = computed(() =>
  simulateContentPassOrContentPass.value
    ? t('privacyShield.video.description.withPurSub')
    : [
        t('privacyShield.video.description.withoutPurSub'),
        t('privacyShield.description.withoutPurSub.withdrawal'),
        t('privacyShield.description.withoutPurSub.validity')
      ]
)

const scriptLoaded = ref(false)

const shouldLoadScript = computed(() =>
  !simulateContentPassOrContentPass.value &&
  sourcepointStore.consentedVendors.length > 0 &&
  sourcepointStore.consentsExist &&
  !sourcepointStore.noScripts &&
  sourcepointStore.consentString &&
  !sourcepointStore.scriptInjected &&
  isClient
)

const videoScript = computed(() => {
  if (shouldLoadScript.value && !scriptLoaded.value && !process.env.SSR) {
    return [
      {
        src: process.env.VIDEO_SCRIPT_URI,
        async: true,
        defer: true,
        'data-vmid': 'video-player',
        key: 'video-player',
        onload: () => {
          scriptLoaded.value = true
          setTimeout(() => {
            if (window.reloadVideoPlayer) {
              window.reloadVideoPlayer()
            }
          }, 100)
        }
      }
    ]
  }
  return []
})

// Call useHead at the top level with computed script
useHead({
  script: videoScript
})

onMounted(() => {
  if (scriptLoaded.value && window.reloadVideoPlayer) {
    window.reloadVideoPlayer()
  }
})

const clearIntervalForFetchAndDispatchAdTargetingStringToPlayer = () => {
  clearInterval(intervalForFetchAndDispatchAdTargetingStringToPlayer?.value ?? undefined)
}

// fetch ad targeting string from molten bundle and dispatch to player
const fetchAndDispatchAdTargetingStringToPlayer = () => {
  // no molten bundle fun without consent
  if (!shouldLoadScript.value && !window?.MoltenBundle && !window?.MoltenBundle?.cmd) { return }
  clearIntervalForFetchAndDispatchAdTargetingStringToPlayer()
  window.MoltenBundle = window.MoltenBundle || {}
  window.MoltenBundle.cmd = window.MoltenBundle.cmd || []

  queueAdEvent(async (moltenBundle) => {
    try {
      const sxpKeys = await moltenBundle.Video.getSXPKeysAsync()
      // adTargetingString gets passed over event to video-player
      const event = new CustomEvent('renderingMoltenBundleReady',
        {
          detail: {
            adTargetingString: sxpKeys
          }
        })
      document.dispatchEvent(event)
    } catch (e) {
      console.warn('error fetching molten bundle video sxp keys:', e)
    }
  })
}

onMounted(() => {
  intervalForFetchAndDispatchAdTargetingStringToPlayer.value = setInterval(async () => {
    await fetchAndDispatchAdTargetingStringToPlayer()
  }, 100)
  setTimeout(() => { clearIntervalForFetchAndDispatchAdTargetingStringToPlayer() }, 30000)
})

const placeholderSrc = computed(() => {
  return (createImgUrl({
    image: props.video.thumbnail,
    brand: 'all',
    imageServiceUrl: configStore.rsConfig.imageServiceUrl as string
  }, props.video.thumbnail.width))?.href ?? ''
})

const autostart = computed(() => {
  // all other autostart logic is handled by the video player itself
  // video articles should always autostart
  return (pageStore?.pageMeta?.articleType === 'video' || props.video.autoPlay)?.toString() ?? 'false'
})

const aspectRatio = computed(() => {
  return props.video.aspectRatio ? `aspect-${props.video.aspectRatio}` : ''
})
</script>

<template>
  <ConsentWrapper
    vendor="video"
    :vendor-id="video.capiId"
    :simulate-content-pass-or-content-pass="simulateContentPassOrContentPass">
    <template #enabled>
      <div :class="`relative ${aspectRatio}`">
        <div class="h-full overflow-hidden">
          <img
            v-if="placeholderSrc"
            class="w-full h-full object-cover block filter blur-lg brightness-100"
            :src="placeholderSrc"
            :width="video.thumbnail.width"
            :height="video.thumbnail.height"
            :title="$t('blockVideo.imgPlaceholder')"
            :alt="$t('blockVideo.imgPlaceholder')">
        </div>
        <div class="absolute inline-block w-full left-0 top-0"
             :class="!placeholderSrc ? 'relative' : ''">
          <div
            class="bdu-widget bdu-player"
            data-bdu="bdu"
            :data-bdu-jwid="video.jwId"
            :data-bdu-consentstring="sourcepointStore.consentString"
            :data-bdu-allowads="video.allowAds"
            :data-bdu-adzone="pageStore?.pageData?.metaData?.adAAdZone"
            :data-bdu-showrecommendations="video.showRecos"
            :data-bdu-autostart="autostart"
            :data-bdu-capi-id="video.capiId" />
        </div>

        <!-- Provider info -->
        <span
          v-if="video.videoProvider?.length"
          class="block mt-2">
          Video: {{ video.videoProvider }}
        </span>
      </div>
    </template>

    <!-- Privacy shield -->
    <template #disabled="{ onConsentClick }">
      <div class="w-full flex justify-center">
        <PrivacyShield
          embed-type="video"
          :simulate-content-pass-or-content-pass="simulateContentPassOrContentPass"
          :headline="headline"
          :description="description"
          :consent-button-text="t('privacyShield.buttons.video.consent')"
          :privacy-policy-button-text="t('privacyShield.buttons.privacyPolicy')"
          @consent-click="onConsentClick" />
      </div>
    </template>
  </ConsentWrapper>
</template>
